import React, {
  useState,
  useLayoutEffect,
  useCallback,
  useId,
  useMemo,
} from 'react';
import AnchoredTourCallout from './AnchoredTourCallout';
import config from '../config';
import './BrowseMapOnboarding.css';

const PADDING = 8;
const CALLOUT_MAX_W = 280;

function expandRect(rect) {
  if (!rect || rect.width <= 0 || rect.height <= 0) return null;
  return {
    left: Math.max(0, rect.left - PADDING),
    top: Math.max(0, rect.top - PADDING),
    width: rect.width + PADDING * 2,
    height: rect.height + PADDING * 2,
  };
}

/**
 * Browse onboarding: dim overlay with two spotlight holes (timeline + map or marker)
 * and two Floating UI callouts (no buttons — tour ends via map interactions).
 */
export default function BrowseMapOnboarding({
  visible,
  timelineRef,
  mapRef,
  hasVisibleMarker,
}) {
  const maskUid = useId().replace(/:/g, '');
  const maskHref = `url(#browse-onboarding-mask-${maskUid})`;
  const [holes, setHoles] = useState([]);
  const [layoutTick, setLayoutTick] = useState(0);

  const mapVirtualRef = useMemo(
    () => ({
      get contextElement() {
        return mapRef?.current?.getContainer?.() ?? undefined;
      },
      getBoundingClientRect() {
        const el = mapRef?.current?.getContainer?.();
        if (!el) {
          return new DOMRect(0, 0, 0, 0);
        }
        const r = el.getBoundingClientRect();
        const s = 72;
        const cx = r.left + r.width * 0.5;
        const cy = r.top + r.height * 0.45;
        return new DOMRect(cx - s / 2, cy - s / 2, s, s);
      },
    }),
    [mapRef],
  );

  const measure = useCallback(() => {
    if (!visible) return;

    const nextHoles = [];

    let t = null;
    if (timelineRef?.current) {
      t = expandRect(timelineRef.current.getBoundingClientRect());
      if (t) nextHoles.push(t);
    }

    let mapHole = null;
    if (hasVisibleMarker) {
      const el = document.getElementById('tour-marker-anchor');
      if (el) {
        mapHole = expandRect(el.getBoundingClientRect());
      }
    }
    const container = mapRef?.current?.getContainer?.();
    if (!mapHole && container) {
      mapHole = expandRect(container.getBoundingClientRect());
    }
    if (mapHole) nextHoles.push(mapHole);

    setHoles(nextHoles);
    setLayoutTick((n) => n + 1);
  }, [visible, timelineRef, mapRef, hasVisibleMarker]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useLayoutEffect(() => {
    if (!visible) return undefined;
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    const id = window.setInterval(measure, 400);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
      window.clearInterval(id);
    };
  }, [visible, measure]);

  const w = typeof window !== 'undefined' ? window.innerWidth : 0;
  const h = typeof window !== 'undefined' ? window.innerHeight : 0;

  const timelineEl = timelineRef?.current ?? null;
  const markerEl =
    hasVisibleMarker && typeof document !== 'undefined'
      ? document.getElementById('tour-marker-anchor')
      : null;
  const mapContainer = mapRef?.current?.getContainer?.() ?? null;
  const mapReference = markerEl ?? (mapContainer ? mapVirtualRef : null);

  const ready = Boolean(
    visible && holes.length >= 2 && timelineEl && mapReference && w && h,
  );

  if (!visible || !w || !h) return null;

  return (
    <>
      {holes.length > 0 && (
        <div className="browse-onboarding-root" aria-hidden={ready ? 'true' : undefined}>
          <svg className="browse-onboarding-svg" width={w} height={h}>
            <defs>
              <mask id={`browse-onboarding-mask-${maskUid}`} maskUnits="userSpaceOnUse">
                <rect x={0} y={0} width={w} height={h} fill="white" />
                {holes.map((r, i) => (
                  <rect
                    key={i}
                    x={r.left}
                    y={r.top}
                    width={r.width}
                    height={r.height}
                    rx={6}
                    ry={6}
                    fill="black"
                  />
                ))}
              </mask>
            </defs>
            <rect
              x={0}
              y={0}
              width={w}
              height={h}
              fill="rgba(0, 0, 0, 0.4)"
              mask={maskHref}
            />
          </svg>
        </div>
      )}

      {ready && (
        <div className="browse-onboarding-callouts" role="region" aria-label="Map browsing tips">
          <AnchoredTourCallout
            show
            reference={timelineEl}
            placement="bottom"
            fallbackPlacements={['top', 'right', 'left']}
            layoutTick={layoutTick}
            maxWidth={CALLOUT_MAX_W}
          >
            <p className="tour-callout__title">Change era with the timeline</p>
            <p className="tour-callout__body">
              {config.ENTRY_TYPE_SHAPES_ENABLED
                ? 'Squares are histories; circles are memories. Solid markers have stories in the selected era; hollow markers have stories in another era.'
                : 'Solid squares have stories in the selected era; hollow squares have stories in another era.'}
            </p>
          </AnchoredTourCallout>

          <AnchoredTourCallout
            show
            reference={mapReference}
            placement="left"
            fallbackPlacements={['right', 'top', 'bottom']}
            layoutTick={layoutTick}
            maxWidth={CALLOUT_MAX_W}
          >
            <p className="tour-callout__title">Open a story from the map</p>
            <p className="tour-callout__body">
              Select a marker or shaded region to read place-based stories.
            </p>
          </AnchoredTourCallout>
        </div>
      )}
    </>
  );
}
